import { useMemo } from "react";
import { DataTable } from "@/components/DataTable";
import { createWeaponColumns, WEAPON_COLUMN_LABELS, WEAPON_COLUMN_TYPES } from "@/lib/table/weapon-columns";
import type { MergedAmmoData } from "@/lib/types/weapon";
import { Swords } from "lucide-react";
import type { FilterFn } from "@tanstack/react-table";

export function WeaponsTable() {
  const columns = useMemo(() => createWeaponColumns(), []);

  // Global filter function for ammo
  const globalFilterFn: FilterFn<MergedAmmoData> = (row, columnId, filterValue: string) => {
    const search = filterValue.toLowerCase();
    const ammo = row.original;
    // Search in ammo name and descriptor name
    const ammoName = ammo.name?.toLowerCase() || "";
    const ammoDescriptor = ammo.descriptorName?.toLowerCase() || "";
    return (
      ammoName.includes(search) ||
      ammoDescriptor.includes(search)
    );
  };

  // Data parser for weapons API response
  // Normalized structure: { ammunition: { [descriptorName]: MergedAmmoData }, weapons: { ... } }
  const dataParser = (data: any): MergedAmmoData[] => {
    if (Array.isArray(data)) {
      return data;
    }
    
    // Handle normalized structure - extract ammunition collection
    if (data && typeof data === 'object' && 'ammunition' in data) {
      const ammunitionCollection = data.ammunition;
      if (ammunitionCollection && typeof ammunitionCollection === 'object') {
        // Convert object to array
        return Object.values(ammunitionCollection) as MergedAmmoData[];
      }
    }
    
    // If structure doesn't match expected format, return empty array
    console.warn('Unexpected weapons data structure:', data);
    return [];
  };

  // Initial column visibility - hide these columns by default
  const initialColumnVisibility = useMemo(() => ({
    descriptorName: false,
    minMaxCategory: false,
  }), []);

  return (
    <DataTable<MergedAmmoData>
      columns={columns}
      columnLabels={WEAPON_COLUMN_LABELS}
      columnTypes={WEAPON_COLUMN_TYPES}
      apiEndpoint="/api/weapons"
      storagePrefix="weapons"
      title="Weapons"
      icon={<Swords className="h-6 w-6" />}
      description="Browse and search through all available weapons"
      globalFilterFn={globalFilterFn}
      emptyMessage="No weapons available"
      emptyFilteredMessage="No weapons found matching your filters"
      itemName="weapons"
      dataParser={dataParser}
      initialColumnVisibility={initialColumnVisibility}
    />
  );
}

